import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Trash2, Trophy, ChevronLeft, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ConfettiEffect from "./ConfettiEffect";
import type { MovieData } from "./SwipeCard";

interface PickFlowModalProps {
  open: boolean;
  starredItems: MovieData[];
  onClose: () => void;
  onPicked: (movie: MovieData) => void;
  onRemoveStar?: (id: number) => void;
  onDone?: () => void;
}

const PickFlowModal = ({ open, starredItems, onClose, onPicked, onRemoveStar, onDone }: PickFlowModalProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [phase, setPhase] = useState<"confirm" | "result">("confirm");
  const [winner, setWinner] = useState<MovieData | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const runnersUp = winner ? starredItems.filter((m) => m.id !== winner.id) : [];

  const handlePickRandom = () => {
    if (starredItems.length === 0) return;
    const picked = starredItems[Math.floor(Math.random() * starredItems.length)];
    setWinner(picked);
    setPhase("result");
    onPicked(picked);
  };

  const handleRemove = (id: number) => {
    onRemoveStar?.(id);
    if (starredItems.length <= 1) {
      onClose();
    }
  };

  const scroll = (dir: number) => {
    scrollRef.current?.scrollBy({ left: dir * 140, behavior: "smooth" });
  };

  const handleDone = () => {
    onClose();
    if (onDone) {
      onDone();
    } else {
      navigate("/");
    }
  };

  if (!open) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-background/90 backdrop-blur-md flex items-center justify-center px-4"
    >
      {phase === "result" && <ConfettiEffect />}
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", stiffness: 300, damping: 25 }}
        className="w-full max-w-sm bg-gradient-card border border-border rounded-2xl shadow-card overflow-hidden relative max-h-[85vh] flex flex-col"
      >
        {phase === "confirm" ? (
          <>
            <div className="flex items-center justify-between p-4 border-b border-border shrink-0">
              <h3 className="font-display text-lg font-bold text-foreground">{t("pickFlow.yourPicks")}</h3>
              <button onClick={onClose} className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center">
                <X className="w-4 h-4 text-muted-foreground" />
              </button>
            </div>
            <p className="px-4 pt-3 text-xs text-muted-foreground">
              {t("pickFlow.happyWithThese")}
            </p>
            <div className="p-4 flex-1 min-h-0 overflow-y-auto space-y-2" style={{ scrollbarWidth: 'none' }}>
              {starredItems.map((movie) => (
                <div
                  key={movie.id}
                  className="flex items-center gap-3 p-3 rounded-xl bg-secondary/50 border border-border"
                >
                  <img src={movie.poster} alt={movie.title} className="w-12 rounded-lg object-cover shrink-0" style={{ height: 72 }} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{movie.title}</p>
                    <p className="text-xs text-muted-foreground">
                      {movie.year} · {movie.runtime} min
                      {movie.certification && (
                        <span className="ml-1 inline-flex items-center px-1 py-0.5 rounded bg-muted text-[9px] font-semibold border border-border/50 align-middle">
                          {movie.certification}
                        </span>
                      )}
                    </p>
                    {movie.providers && movie.providers.length > 0 && (
                      <div className="flex items-center gap-1 mt-1 flex-wrap">
                        {movie.providers.map((p) => {
                          const inner = (
                            <div className={`flex items-center gap-1 px-1.5 py-0.5 rounded bg-secondary/80 ${movie.watch_link ? "hover:bg-secondary hover:ring-1 hover:ring-primary/30 cursor-pointer" : ""}`}>
                              {p.logo_path && <img src={p.logo_path} alt={p.provider_name} className="w-4 h-4 rounded-sm" />}
                              <span className="text-[9px] text-muted-foreground">{p.provider_name}</span>
                            </div>
                          );
                          return movie.watch_link ? (
                            <a key={p.provider_id} href={movie.watch_link} target="_blank" rel="noopener noreferrer">{inner}</a>
                          ) : (
                            <div key={p.provider_id}>{inner}</div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => handleRemove(movie.id)}
                    className="w-8 h-8 rounded-full bg-destructive/10 hover:bg-destructive/20 flex items-center justify-center shrink-0 transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5 text-destructive" />
                  </button>
                </div>
              ))}
            </div>
            <div className="p-4 border-t border-border shrink-0">
              <button
                onClick={handlePickRandom}
                disabled={starredItems.length === 0}
                className="w-full py-3 rounded-full bg-gradient-iris text-primary-foreground font-semibold text-sm shadow-iris active:scale-95 transition-transform disabled:opacity-40 disabled:pointer-events-none"
              >
                {t("pickFlow.pickWatching")}
              </button>
            </div>
          </>
        ) : (
          <div className="flex-1 min-h-0 overflow-y-auto flex flex-col" style={{ scrollbarWidth: 'none' }}>
            <div className="p-4 text-center space-y-3 shrink-0">
              <Trophy className="w-8 h-8 text-star mx-auto" />
              <h2 className="font-display text-xl font-bold text-foreground">{t("pickFlow.youreWatching")}</h2>
              {winner && (
                <div className="p-3 rounded-xl bg-primary/10 border border-primary/30">
                  <div className="flex gap-3 text-left">
                    <img src={winner.poster} alt={winner.title} className="w-20 h-[120px] rounded-lg object-cover shadow-lg shrink-0" />
                    <div className="flex-1 min-w-0 flex flex-col justify-center">
                      <h3 className="font-display font-bold text-foreground text-base leading-tight">{winner.title}</h3>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {winner.year} · {winner.runtime} min · ⭐ {winner.rating}
                        {winner.certification && (
                          <span className="ml-1 inline-flex items-center px-1 py-0.5 rounded bg-muted text-[9px] font-semibold border border-border/50 align-middle">
                            {winner.certification}
                          </span>
                        )}
                      </p>
                      {winner.providers && winner.providers.length > 0 && (
                        <div className="mt-2">
                          <p className="text-[9px] text-muted-foreground tracking-wider uppercase mb-0.5">{t("pickFlow.streamOn")}</p>
                          <div className="flex flex-wrap gap-1">
                            {winner.providers.map((p) => {
                              const inner = (
                                <div className={`flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-secondary/60 ${winner.watch_link ? "hover:bg-secondary hover:ring-1 hover:ring-primary/30 cursor-pointer" : ""}`}>
                                  {p.logo_path && <img src={p.logo_path} alt={p.provider_name} className="w-4 h-4 rounded" />}
                                  <span className="text-[9px] text-foreground font-medium">{p.provider_name}</span>
                                </div>
                              );
                              return winner.watch_link ? (
                                <a key={p.provider_id} href={winner.watch_link} target="_blank" rel="noopener noreferrer">{inner}</a>
                              ) : (
                                <div key={p.provider_id}>{inner}</div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {runnersUp.length > 0 && (
              <div className="px-4 pb-3">
                <p className="text-[11px] text-muted-foreground tracking-wider mb-1.5">{t("pickFlow.otherPicks")}</p>
                <div className="relative">
                  {runnersUp.length > 2 && (
                    <>
                      <button
                        onClick={() => scroll(-1)}
                        className="absolute -left-2 top-1/2 -translate-y-1/2 z-10 w-6 h-6 rounded-full bg-secondary/80 backdrop-blur-sm flex items-center justify-center text-foreground shadow-md"
                      >
                        <ChevronLeft className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => scroll(1)}
                        className="absolute -right-2 top-1/2 -translate-y-1/2 z-10 w-6 h-6 rounded-full bg-secondary/80 backdrop-blur-sm flex items-center justify-center text-foreground shadow-md"
                      >
                        <ChevronRight className="w-3.5 h-3.5" />
                      </button>
                    </>
                  )}
                  <div
                    ref={scrollRef}
                    className="flex gap-2 overflow-x-auto scrollbar-hide pb-1 px-1"
                    style={{ scrollSnapType: "x mandatory" }}
                  >
                    {runnersUp.map((item) => (
                      <div
                        key={item.id}
                        className="flex-shrink-0 w-[100px] rounded-lg bg-secondary/50 p-1.5 text-center"
                        style={{ scrollSnapAlign: "start" }}
                      >
                        <img src={item.poster} alt={item.title} className="w-full h-[110px] rounded object-cover mb-1" />
                        <p className="text-[10px] text-foreground font-medium truncate">{item.title}</p>
                        <p className="text-[9px] text-muted-foreground mt-0.5">
                          {item.year}
                          {item.certification && (
                            <span className="ml-0.5 inline-flex items-center px-1 py-0.5 rounded bg-muted text-[8px] font-semibold border border-border/50 align-middle">
                              {item.certification}
                            </span>
                          )}
                        </p>
                        {item.providers && item.providers.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1 justify-center">
                            {item.providers.map((p) => {
                              const inner = p.logo_path ? (
                                <img src={p.logo_path} alt={p.provider_name} className={`w-5 h-5 rounded ${item.watch_link ? "hover:scale-110 transition-transform cursor-pointer" : ""}`} />
                              ) : (
                                <span className="text-[9px] text-muted-foreground px-1 py-0.5 rounded bg-secondary/60">{p.provider_name}</span>
                              );
                              return item.watch_link ? (
                                <a key={p.provider_id} href={item.watch_link} target="_blank" rel="noopener noreferrer" title={p.provider_name}>{inner}</a>
                              ) : (
                                <div key={p.provider_id} title={p.provider_name}>{inner}</div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div className="p-4 border-t border-border shrink-0 mt-auto">
              <button
                onClick={handleDone}
                className="w-full py-3 rounded-full bg-gradient-iris text-primary-foreground font-semibold text-sm shadow-iris active:scale-95 transition-transform"
              >
                {t("pickFlow.letsWatchIt")}
              </button>
            </div>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

export default PickFlowModal;
